import { useState, useEffect } from 'react';
import { seriesAPI } from '../../services/api';
import { PageHeader, SectionTitle, Field, DataTable, StatusBadge, Modal, ConfirmDialog, DayPicker, Spinner, EmptyState } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const EMPTY_FORM = {
  name: '', short_name: '', season_year: '2026', max_overs: '',
  start_date: '', end_date: '', series_type: 'League',
  play_days: ['Sun'], structure: 'divisions_pools',
  cricclubs_id: '', status: 'draft'
};

const STRUCTURE_OPTIONS = [
  { value: 'divisions_pools', label: 'Has Divisions & Pools', desc: 'T20 Premier / Div I / Div II / Div III' },
  { value: 'no_divisions',    label: 'No Divisions',          desc: 'F40, T30 — all teams in one pool' },
  { value: 'groups_only',     label: 'Groups Only',           desc: 'GLT — group stage, no divisions' },
];

const OVERS_PRESETS = ['20', '30', '40'];

export default function SeriesPage() {
  const { canEdit, isAdmin } = useAuthStore();
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { loadSeries(); }, []);

  const loadSeries = async () => {
    setLoading(true);
    try {
      const { data } = await seriesAPI.list();
      setSeriesList(data);
    } catch (err) {
      toast.error('Failed to load series');
    } finally { setLoading(false); }
  };

  const setField = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setForm({
      name: s.name, short_name: s.short_name,
      season_year: String(s.season_year), max_overs: String(s.max_overs),
      start_date: s.start_date?.split('T')[0] || '',
      end_date: s.end_date?.split('T')[0] || '',
      series_type: s.series_type || 'League',
      play_days: s.play_days || ['Sun'],
      structure: s.structure || 'divisions_pools',
      cricclubs_id: s.cricclubs_id || '',
      status: s.status || 'draft'
    });
    setEditingId(s.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.short_name || !form.season_year || !form.max_overs)
      return toast.error('Fill in all required fields');
    if (!form.start_date || !form.end_date)
      return toast.error('Start and end dates are required');
    if (form.play_days.length === 0)
      return toast.error('Select at least one play day');
    setSaving(true);
    try {
      const payload = { ...form, season_year: parseInt(form.season_year), max_overs: parseInt(form.max_overs), cricclubs_id: form.cricclubs_id || null };
      if (editingId) { await seriesAPI.update(editingId, payload); toast.success('Series updated'); }
      else { await seriesAPI.create(payload); toast.success('Series created'); }
      setShowForm(false);
      loadSeries();
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await seriesAPI.delete(showDeleteConfirm);
      toast.success('Series deleted');
      setShowDeleteConfirm(null);
      loadSeries();
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const structureTag = (s) => ({
    divisions_pools: <span className="tag tag-blue">Div + Pools</span>,
    no_divisions:    <span className="tag tag-gold">No Divisions</span>,
    groups_only:     <span className="tag tag-purple">Groups Only</span>,
  }[s] || <span className="tag tag-blue">{s}</span>);

  const totalTeams = seriesList.reduce((a, s) => a + (parseInt(s.team_count) || 0), 0);
  const activeCount = seriesList.filter(s => s.status === 'active').length;

  return (
    <div>
      <PageHeader icon="◈" title="Series"
        subtitle="Top-level entity. T20/F40/T30 = Divisions + Pools. GLT = Groups Only."
        actions={canEdit() && <button className="btn btn-primary" onClick={openCreate}>+ Create Series</button>}
      />
      <div className="grid-4" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total Series', value: seriesList.length, sub: '2026 season', color: 'var(--accent)' },
          { label: 'Active', value: activeCount, sub: 'Currently running', color: 'var(--blue)' },
          { label: 'Total Teams', value: totalTeams, sub: 'Across all series', color: 'var(--gold)' },
          { label: 'Draft', value: seriesList.filter(s => s.status === 'draft').length, sub: 'Not yet active', color: 'var(--purple)' },
        ].map(s => (
          <div key={s.label} className="card stat-card">
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>
      <div className="card" style={{ padding: 20 }}>
        <SectionTitle>All Series — 2026 Season</SectionTitle>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={28} /></div>
        ) : seriesList.length === 0 ? (
          <EmptyState icon="◈" title="No series yet"
            description="Create your first series to get started — T20, F40, T30 or GLT."
            action={canEdit() && <button className="btn btn-primary" onClick={openCreate}>+ Create Series</button>}
          />
        ) : (
          <DataTable
            headers={['Series', 'Short', 'Overs', 'Structure', 'Play Days', 'Date Range', 'Teams', 'Divisions', 'Status', ...(canEdit() ? ['Actions'] : [])]}
            rows={seriesList.map(s => [
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{s.name}</div>
                {s.cricclubs_id && <div className="mono" style={{ fontSize: 10, color: 'var(--mid)', marginTop: 2 }}>CC: {s.cricclubs_id}</div>}
              </div>,
              <span className="mono" style={{ fontWeight: 700, color: 'var(--accent)' }}>{s.short_name}</span>,
              <span className="mono">{s.max_overs}</span>,
              structureTag(s.structure),
              <span style={{ fontSize: 12 }}>{(s.play_days || []).join(' · ')}</span>,
              <span style={{ fontSize: 11, color: 'var(--mid)' }}>
                {s.start_date ? new Date(s.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                {' → '}
                {s.end_date ? new Date(s.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
              </span>,
              <span className="mono">{s.team_count || 0}</span>,
              <span className="mono">{s.division_count || 0}</span>,
              <StatusBadge status={s.status} />,
              ...(canEdit() ? [
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}>Edit</button>
                  {isAdmin() && <button className="btn btn-danger btn-sm" onClick={() => setShowDeleteConfirm(s.id)}>Delete</button>}
                </div>
              ] : [])
            ])}
          />
        )}
      </div>
      <div className="card" style={{ padding: 20, marginTop: 16 }}>
        <SectionTitle color="var(--mid)">2026 Season Reference</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          {[
            { short: 'T20', name: 'T20 League', overs: 20, day: 'Sunday',    structure: 'Div + Pools',    color: 'var(--accent)' },
            { short: 'F40', name: 'F40 League', overs: 40, day: 'Saturday',  structure: 'No Divisions',   color: 'var(--gold)' },
            { short: 'T30', name: 'T30 League', overs: 30, day: 'Saturday',  structure: 'No Divisions',   color: 'var(--blue)' },
            { short: 'GLT', name: 'GL Trophy',  overs: 20, day: 'Sat + Sun', structure: 'Groups Only',    color: 'var(--purple)' },
          ].map(s => (
            <div key={s.short} style={{ padding: 14, borderRadius: 8, border: '1px solid var(--border)', borderLeft: `3px solid ${s.color}` }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: s.color, fontFamily: 'monospace' }}>{s.short}</div>
              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4 }}>{s.name}</div>
              <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 6, lineHeight: 1.7 }}>
                <div>{s.overs} overs · {s.day}</div>
                <div>{s.structure}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showForm && (
        <Modal title={editingId ? 'Edit Series' : 'Create New Series'} onClose={() => setShowForm(false)} width={640}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Series Name" required>
                <input className="input" placeholder="e.g. T20 League 2026" value={form.name} onChange={e => setField('name', e.target.value)} />
              </Field>
            </div>
            <Field label="Short Name" required>
              <input className="input" placeholder="e.g. T20" value={form.short_name} onChange={e => setField('short_name', e.target.value.toUpperCase())} />
            </Field>
            <Field label="Season Year" required>
              <input className="input" placeholder="2026" value={form.season_year} onChange={e => setField('season_year', e.target.value)} />
            </Field>
            <Field label="Max Overs" required note="T20=20, T30=30, F40=40">
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                {OVERS_PRESETS.map(o => (
                  <button key={o} type="button" className={`btn btn-sm ${form.max_overs === o ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setField('max_overs', o)}>{o}</button>
                ))}
              </div>
              <input className="input" placeholder="or type custom" value={form.max_overs} onChange={e => setField('max_overs', e.target.value)} />
            </Field>
            <Field label="Series Type">
              <select className="input select" value={form.series_type} onChange={e => setField('series_type', e.target.value)}>
                <option>League</option><option>Tournament</option><option>Cup</option>
              </select>
            </Field>
            <Field label="Status">
              <select className="input select" value={form.status} onChange={e => setField('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </Field>
            <Field label="Start Date" required>
              <input className="input" type="date" value={form.start_date} onChange={e => setField('start_date', e.target.value)} />
            </Field>
            <Field label="End Date" required>
              <input className="input" type="date" value={form.end_date} onChange={e => setField('end_date', e.target.value)} />
            </Field>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Play Days" note="Days of the week when matches are scheduled">
                <DayPicker value={form.play_days} onChange={v => setField('play_days', v)} />
              </Field>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Series Structure">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {STRUCTURE_OPTIONS.map(opt => (
                    <div key={opt.value} onClick={() => setField('structure', opt.value)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 7, cursor: 'pointer', border: `1.5px solid ${form.structure === opt.value ? 'var(--accent)' : 'var(--border)'}`, background: form.structure === opt.value ? 'rgba(0,200,150,.07)' : 'transparent', transition: 'all .15s' }}>
                      <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, border: `2px solid ${form.structure === opt.value ? 'var(--accent)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {form.structure === opt.value && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} />}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: form.structure === opt.value ? 'var(--accent)' : 'var(--text)' }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--mid)' }}>{opt.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Field>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="CricClubs Series ID" note="Optional — from cricclubs.com URL ?series_id=XXX">
                <input className="input mono" placeholder="e.g. 113" value={form.cricclubs_id} onChange={e => setField('cricclubs_id', e.target.value)} />
              </Field>
            </div>
          </div>
          <hr className="divider" />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><Spinner size={14} /> Saving...</> : editingId ? '✓ Update Series' : '+ Create Series'}
            </button>
          </div>
        </Modal>
      )}

      {showDeleteConfirm && (
        <ConfirmDialog danger
          message="Delete this series? This will also delete all divisions, teams, and fixtures associated with it. This cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(null)}
        />
      )}
    </div>
  );
}
